import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../ui/atoms';
import { useTranslation } from 'react-i18next';
import { formatDisplayDateTime } from '../../../utils/dateFormatting';

// Each service (Writer, Agent, Podcast, Paqshot) is sold separately: Starter / Pro + Enterprise.
// A plan grants monthly quotas per unit (articles, images, replies, episodes); the free trial grants
// small quotas of every service. Prices, quotas and features come from the billing-core catalog (Stripe).

const SERVICE_TAB_STORAGE_KEY = 'billing_service_tab';
const CURRENCY_ORDER = ['pln', 'usd', 'eur'];
const ACTIVE_STATUSES = ['active', 'trialing', 'past_due'];
const ENTERPRISE_URL = 'https://prosecaptain.com/kontakt';
// Purchases stay hidden until billing-core goes live (set REACT_APP_BILLING_ENABLED=true).
const BILLING_ENABLED = process.env.REACT_APP_BILLING_ENABLED === 'true';

const defaultCurrencyForLanguage = (language) => (String(language || '').startsWith('pl') ? 'pln' : 'usd');

const formatMinorAmount = (unitAmount, currency, language) => {
  if (typeof unitAmount !== 'number' || !currency) return null;
  try {
    return new Intl.NumberFormat(language || 'en', {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: unitAmount % 100 === 0 ? 0 : 2
    }).format(unitAmount / 100);
  } catch (_) {
    return `${(unitAmount / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
};

const readStoredService = () => {
  try {
    return localStorage.getItem(SERVICE_TAB_STORAGE_KEY);
  } catch (_) {
    return null;
  }
};

const SubscriptionTab = ({
  billingStatus,
  handleOpenBillingPortal,
  handleStartSubscription,
  handleStartTopup
}) => {
  const { t, i18n } = useTranslation();
  const serviceOrder = useMemo(() => {
    const order = Array.isArray(billingStatus?.service_order) ? billingStatus.service_order : [];
    return order.filter((service) => billingStatus?.services?.[service]);
  }, [billingStatus]);

  const [activeService, setActiveService] = useState(readStoredService);
  useEffect(() => {
    if (serviceOrder.length && !serviceOrder.includes(activeService)) {
      setActiveService(serviceOrder[0]);
    }
  }, [serviceOrder, activeService]);
  const selectService = (service) => {
    setActiveService(service);
    try {
      localStorage.setItem(SERVICE_TAB_STORAGE_KEY, service);
    } catch (_) {
      // ignore storage errors
    }
  };

  const serviceData = billingStatus?.services?.[activeService] || null;
  const plans = useMemo(() => (Array.isArray(serviceData?.plans) ? serviceData.plans : []), [serviceData]);
  const topups = useMemo(() => (Array.isArray(serviceData?.topups) ? serviceData.topups : []), [serviceData]);

  const availableCurrencies = useMemo(() => {
    const found = new Set([...plans, ...topups].map((item) => String(item?.currency || '').toLowerCase()).filter(Boolean));
    return CURRENCY_ORDER.filter((code) => found.has(code))
      .concat([...found].filter((code) => !CURRENCY_ORDER.includes(code)));
  }, [plans, topups]);
  const [currency, setCurrency] = useState(() => defaultCurrencyForLanguage(i18n.language));
  useEffect(() => {
    if (availableCurrencies.length && !availableCurrencies.includes(currency)) {
      const preferred = defaultCurrencyForLanguage(i18n.language);
      setCurrency(availableCurrencies.includes(preferred) ? preferred : availableCurrencies[0]);
    }
  }, [availableCurrencies, currency, i18n.language]);

  const formatPrice = (unitAmount) => formatMinorAmount(unitAmount, currency, i18n.language);
  const formatDate = (value) => (value ? formatDisplayDateTime(value, i18n.language) : t('common.noData'));
  const unitLabel = (unit, count) => t(`userProfile.subscription.units.${unit}`, { count, defaultValue: `${count} ${unit}` });
  const currencyPlans = plans.filter((plan) => String(plan?.currency || '').toLowerCase() === currency);
  const currencyTopups = topups.filter((pack) => String(pack?.currency || '').toLowerCase() === currency);

  const subscription = serviceData?.subscription || {};
  const subscriptionStatus = subscription.status || 'inactive';
  const isActiveSubscription = ACTIVE_STATUSES.includes(subscriptionStatus);
  const cancelAtPeriodEnd = subscription.cancel_at_period_end === true;
  const activePlan = plans.find((plan) => plan.price_id === subscription.stripe_price_id) || null;
  const planLabel = activePlan?.display_name
    || [serviceData?.display_name, subscription.plan_code].filter(Boolean).join(' ')
    || t('userProfile.subscription.currentPlan');
  const meters = Array.isArray(serviceData?.meters) ? serviceData.meters : [];
  const onTrial = !isActiveSubscription && meters.some((meter) => meter.origin === 'trial' && !meter.trial_expired);
  const trialEnded = !isActiveSubscription && meters.some((meter) => meter.origin === 'trial' && meter.trial_expired);
  const exhausted = meters.length > 0 && meters.some((meter) => meter.available <= 0);

  if (!serviceOrder.length) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-medium leading-7 text-gray-900">{t('userProfile.subscription.detailsTitle')}</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">{t('userProfile.subscription.detailsDescription')}</p>
        </div>
        <p className="text-sm text-gray-500">{t('common.loading', { defaultValue: 'Loading...' })}</p>
      </div>
    );
  }

  const serviceSwitcher = (
    <div className="inline-flex flex-wrap rounded-xl border border-gray-200 bg-gray-50 p-1" role="tablist">
      {serviceOrder.map((service) => {
        const data = billingStatus.services[service];
        const active = ACTIVE_STATUSES.includes(data?.subscription?.status);
        return (
          <button
            key={service}
            type="button"
            role="tab"
            aria-selected={service === activeService}
            onClick={() => selectService(service)}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
              service === activeService ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className={`h-2 w-2 rounded-full ${active ? 'bg-green-500' : 'bg-gray-300'}`} aria-hidden="true" />
            {data?.display_name || service}
          </button>
        );
      })}
    </div>
  );

  const currencySwitcher = availableCurrencies.length > 1 && (
    <div className="inline-flex rounded-lg border border-gray-200 p-0.5" role="group" aria-label={t('userProfile.subscription.currency')}>
      {availableCurrencies.map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => setCurrency(code)}
          aria-pressed={currency === code}
          className={`rounded-md px-3 py-1 text-xs font-semibold uppercase transition-colors ${
            currency === code ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          {code}
        </button>
      ))}
    </div>
  );

  const usage = meters.length > 0 && (
    <div className="border border-gray-200 rounded-xl p-5">
      <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
        {onTrial || trialEnded ? t('userProfile.subscription.trialUsage') : t('userProfile.subscription.includedUsage')}
      </p>
      <div className="mt-4 space-y-4">
        {meters.map((meter) => {
          const percent = meter.limit > 0 ? Math.min(100, Math.round((meter.used / meter.limit) * 100)) : 100;
          const barClass = percent >= 95 ? 'bg-red-600' : percent >= 80 ? 'bg-amber-500' : 'bg-primary-600';
          return (
            <div key={meter.meter}>
              <div className="flex items-center justify-between text-sm text-gray-700">
                <span className="font-medium">{unitLabel(meter.unit, meter.limit)}</span>
                <span>{t('userProfile.subscription.usedOf', { used: meter.used, limit: meter.limit })}</span>
              </div>
              <div className="mt-1.5 h-2 w-full rounded-full bg-gray-200">
                <div className={`h-2 rounded-full transition-all duration-300 ${barClass}`} style={{ width: `${percent}%` }} />
              </div>
              {meter.prepaid > 0 && (
                <p className="mt-1 text-xs text-gray-500">{t('userProfile.subscription.prepaidUnits', { count: meter.prepaid })}</p>
              )}
            </div>
          );
        })}
      </div>
      {meters[0]?.period_end && (
        <p className="mt-4 text-xs text-gray-500">
          {onTrial || trialEnded
            ? t('userProfile.subscription.trialEndsOn', { date: formatDate(meters[0].period_end) })
            : t('userProfile.subscription.nextCreditsReset', { date: formatDate(meters[0].period_end) })}
        </p>
      )}
    </div>
  );

  const planCards = (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500">{t('userProfile.subscription.pickPlan')}</p>
        {currencySwitcher}
      </div>
      {currencyPlans.length ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {currencyPlans.map((plan) => {
            const isPro = plan.plan_code === 'pro';
            const features = plan.features?.length
              ? plan.features
              : Object.entries(plan.grants || {}).map(([unit, count]) => `${unitLabel(unit, count)} / ${t('userProfile.subscription.month')}`);
            return (
              <div
                key={plan.price_id}
                className={`flex flex-col rounded-xl border p-5 ${isPro ? 'border-primary-500 ring-1 ring-primary-200 bg-primary-50/40' : 'border-gray-200 bg-white'}`}
              >
                {isPro && (
                  <span className="mb-2 inline-flex self-start rounded-full bg-primary-600 px-2.5 py-1 text-[10px] font-semibold tracking-wide text-white">
                    {t('userProfile.subscription.mostPopular')}
                  </span>
                )}
                <p className="text-base font-semibold text-gray-900">{plan.display_name}</p>
                <p className="mt-2 text-2xl font-semibold text-gray-900">
                  {t('userProfile.subscription.pricePerMonth', { price: formatPrice(plan.unit_amount) })}
                </p>
                <ul className="mt-4 space-y-1.5 text-sm text-gray-700">
                  {features.map((feature) => (
                    <li key={feature} className="flex gap-2">
                      <span aria-hidden="true" className="text-primary-600">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleStartSubscription(plan.price_id)}
                  size="sm"
                  className="mt-5 h-9 w-full rounded-md shadow-sm"
                  disabled={isActiveSubscription}
                >
                  {t('userProfile.subscription.choosePlan', { plan: plan.display_name })}
                </Button>
              </div>
            );
          })}
          <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-5">
            <p className="text-base font-semibold text-gray-900">Enterprise</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{t('userProfile.subscription.customQuote')}</p>
            <p className="mt-1 text-sm text-gray-600">{t('userProfile.subscription.enterpriseDescription')}</p>
            <a href={ENTERPRISE_URL} target="_blank" rel="noopener noreferrer" className="mt-auto pt-5">
              <Button size="sm" variant="secondary" className="h-9 w-full rounded-md shadow-sm">
                {t('userProfile.subscription.contactSales')}
              </Button>
            </a>
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {t('userProfile.subscription.plansComingSoon')}
        </div>
      )}
    </div>
  );

  const topupSection = currencyTopups.length > 0 && (
    <div className="border border-gray-200 rounded-xl p-5">
      <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{t('userProfile.subscription.topupTitle')}</p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {currencyTopups.map((pack) => (
          <div key={pack.price_id} className="flex flex-col rounded-lg border border-gray-200 p-4">
            <p className="text-base font-semibold text-gray-900">
              {Object.entries(pack.grants || {}).map(([unit, count]) => `+${unitLabel(unit, count)}`).join(', ')}
            </p>
            <p className="mt-1 text-sm text-gray-600">{formatPrice(pack.unit_amount) || '-'}</p>
            <Button onClick={() => handleStartTopup(pack.price_id)} size="sm" className="mt-3">
              {t('userProfile.subscription.buyTopup')}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );

  if (!BILLING_ENABLED) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-medium leading-7 text-gray-900">{t('userProfile.subscription.detailsTitle')}</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">{t('userProfile.subscription.comingSoon')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-medium leading-7 text-gray-900">{t('userProfile.subscription.detailsTitle')}</h2>
        <p className="mt-2 text-sm leading-6 text-gray-600">{t('userProfile.subscription.detailsDescription')}</p>
      </div>

      {serviceSwitcher}

      {serviceData?.billing_hold && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm font-medium text-red-900">{t('userProfile.subscription.billingHoldWarning')}</p>
          <div className="mt-3">
            <Button onClick={handleOpenBillingPortal} size="sm" variant="secondary">
              {t('userProfile.subscription.manageInStripe')}
            </Button>
          </div>
        </div>
      )}

      {isActiveSubscription ? (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="border border-gray-200 rounded-xl p-5">
            <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{t('userProfile.subscription.currentPlan')}</p>
            <p className="mt-2 text-xl font-semibold text-gray-900">{planLabel}</p>
            <p className="mt-2 text-sm text-gray-600">
              {t('common.status')}:{' '}
              <span className={`font-medium ${cancelAtPeriodEnd ? 'text-amber-700' : 'text-green-700'}`}>
                {cancelAtPeriodEnd ? t('userProfile.subscription.cancelingAtPeriodEnd') : subscriptionStatus}
              </span>
            </p>
            <p className="mt-2 text-sm text-gray-600">
              {cancelAtPeriodEnd
                ? t('userProfile.subscription.endsOn', { date: formatDate(subscription.period_end) })
                : t('userProfile.subscription.nextCreditsReset', { date: formatDate(subscription.period_end) })}
            </p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{t('userProfile.subscription.manageTitle')}</p>
            <p className="mt-2 text-sm text-gray-600">{t('userProfile.subscription.manageDescription')}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={handleOpenBillingPortal} size="sm" variant="secondary">
                {t('userProfile.subscription.adjustPlan')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className={`rounded-lg border px-4 py-3 ${onTrial ? 'border-primary-200 bg-primary-50' : 'border-amber-200 bg-amber-50'}`}>
          <p className={`text-sm font-medium ${onTrial ? 'text-primary-900' : 'text-amber-900'}`}>
            {onTrial
              ? t('userProfile.subscription.trialActive', { service: serviceData?.display_name, date: formatDate(meters[0]?.period_end) })
              : t('userProfile.subscription.noActiveForService', { service: serviceData?.display_name || activeService })}
          </p>
        </div>
      )}

      {usage}

      {isActiveSubscription && exhausted && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm font-medium text-amber-900">{t('userProfile.subscription.lowCreditsWarning')}</p>
          <div className="mt-3">
            <Button onClick={handleOpenBillingPortal} size="sm" variant="secondary">
              {t('userProfile.subscription.adjustPlan')}
            </Button>
          </div>
        </div>
      )}

      {!isActiveSubscription && planCards}
      {topupSection}
    </div>
  );
};

export default SubscriptionTab;
